import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Logo1ClinHorizontal } from '../components/Logo1Clin';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <Logo1ClinHorizontal width={140} />
          <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-sm text-teal-400 hover:text-teal-300 transition-colors" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mb-8">Last Updated: March 15, 2026</p>

        <div className="space-y-8 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-white mb-3">1. Introduction</h2>
            <p>Oneprick Labs Private Limited ("Company," "We," "Us," or "Our") is committed to protecting the privacy of our users. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 1clin.com and use our practice management application.</p>
            <p className="mt-2">By using the Platform, you agree to the collection and use of information in accordance with this policy.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">2. Data Roles (DPDP Act 2023 Compliance)</h2>
            <p className="mb-2">Under the Indian Digital Personal Data Protection Act, 2023:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-white">Data Fiduciary:</strong> The Doctor/Clinic is the Data Fiduciary for patient health records. Oneprick Labs is the Data Fiduciary for the Doctor's account information.</li>
              <li><strong className="text-white">Data Processor:</strong> Oneprick Labs acts as a Data Processor regarding the patient health data uploaded by Doctors.</li>
              <li><strong className="text-white">Data Principal:</strong> The individual (Doctor, Patient, or Vendor) to whom the personal data belongs.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">3. Information We Collect</h2>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">A. From Doctors (Healthcare Providers)</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-white">Identity Data:</strong> Full name, NMC/State Medical Council Registration Number, and specialized qualifications.</li>
              <li><strong className="text-white">Clinic Data:</strong> Clinic name, GST/PAN (for billing), physical address, and contact numbers.</li>
              <li><strong className="text-white">Financial Data:</strong> Payment transaction IDs (processed via PayU). We do not store full credit card or CVV details.</li>
            </ul>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">B. From Patients (End Users)</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-white">Health Data (PHI):</strong> Medical history, vitals, symptoms, diagnostic reports, and digital prescriptions.</li>
              <li><strong className="text-white">Contact Data:</strong> Mobile number (for SMS/WhatsApp reminders) and Email ID.</li>
              <li><strong className="text-white">Government IDs:</strong> ABHA ID or other identifiers if integrated by the Doctor for healthcare records.</li>
            </ul>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">C. From Vendors (Labs/Pharmacies)</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-white">Business Data:</strong> Drug license numbers, GST details, shop/lab address, and authorized contact person details.</li>
              <li><strong className="text-white">Transaction Logs:</strong> Records of orders received and fulfilled through the platform.</li>
            </ul>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">D. Automatic Technical Data</h3>
            <p>IP addresses, browser type, device identifiers, and time-stamped "Audit Logs" of every action performed on the platform to prevent unauthorized access.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">4. How We Use Your Information</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-white">Service Delivery:</strong> To generate digital prescriptions, manage appointments, and facilitate billing.</li>
              <li><strong className="text-white">Communication:</strong> To send appointment reminders via SMS/WhatsApp and platform updates.</li>
              <li><strong className="text-white">Compliance:</strong> To maintain medical records for the statutory period (3 years) as required by the NMC.</li>
              <li><strong className="text-white">Security:</strong> To detect and prevent fraud, medical quackery, or unauthorized access.</li>
              <li><strong className="text-white">Health Tracking & Google Fit Integration:</strong> To import and display fitness data (steps, calories, sleep, heart rate) from connected third-party health platforms for personalized health monitoring.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">4A. Google Fit Integration & Health Data</h2>
            <p className="mb-3">1Clin integrates with Google Fit to provide patients with a comprehensive health tracking experience. When a patient voluntarily connects their Google Fit account, we access the following data:</p>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">Data We Collect from Google Fit</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-white">Activity Data:</strong> Daily step count, calories burned, and physical activity duration — used to track patient fitness goals and display activity trends.</li>
              <li><strong className="text-white">Body Metrics:</strong> Weight and body measurements (if available) — used to monitor patient health progress alongside doctor-prescribed targets.</li>
              <li><strong className="text-white">Sleep Data:</strong> Sleep duration, bed time, and wake time — used to track sleep patterns and display sleep quality trends to both patients and their doctors.</li>
              <li><strong className="text-white">Heart Rate Data:</strong> Average heart rate readings (if available from connected wearables) — used to supplement face vitals data and provide comprehensive cardiovascular health insights.</li>
            </ul>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">How We Use Google Fit Data</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>To display health metrics in the patient's Health Tracking dashboard (Overview, Activity, Sleep tabs).</li>
              <li>To enable doctors to monitor their registered patients' fitness progress and set health targets.</li>
              <li>To generate compliance alerts when patients miss doctor-prescribed health goals (steps, calories, sleep).</li>
              <li>To display trends and charts for long-term health monitoring.</li>
            </ul>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">Data Storage & Sharing</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Google Fit data is stored securely on our Indian cloud servers alongside other health records.</li>
              <li>Data is shared only with the patient's registered doctor(s) within the 1Clin platform.</li>
              <li>We do <strong className="text-white">not</strong> sell, share, or transfer Google Fit data to any third-party advertisers or data brokers.</li>
              <li>Patients can disconnect Google Fit at any time from their Health dashboard, which stops all future data syncing.</li>
              <li>Upon disconnection, previously synced data remains in the patient's health record unless they request deletion.</li>
            </ul>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">Google API Services User Data Policy</h3>
            <p>1Clin's use and transfer of information received from Google APIs adheres to the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-teal-400 underline hover:text-teal-300">Google API Services User Data Policy</a>, including the Limited Use requirements.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">5. Data Sovereignty & Storage</h2>
            <p className="mb-2">In strict compliance with Indian Data Localization norms:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>All personal and health data is stored on secure cloud servers located within the territory of India.</li>
              <li>We use AES-256 encryption for data at rest and TLS 1.3 for data in transit.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">6. Sharing of Data</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-white">With Your Consent:</strong> Patient data is shared with Vendors (Labs/Pharmacies) only when the Doctor or Patient explicitly triggers an order.</li>
              <li><strong className="text-white">Third-Party Facilitators:</strong> We share necessary metadata with PayU to process payments.</li>
              <li><strong className="text-white">Legal Mandates:</strong> We may disclose data if required by law, court order, or a government authority (e.g., CERT-In).</li>
              <li><strong className="text-white">No Sale of Data:</strong> Oneprick Labs Private Limited does not sell, rent, or trade your personal or health data to third-party marketing companies.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">7. Your Rights as a Data Principal</h2>
            <p className="mb-2">Under the DPDP Act 2023, you have the following rights:</p>
            <ol className="list-decimal pl-5 space-y-1">
              <li><strong className="text-white">Right to Access:</strong> View a summary of the personal data being processed.</li>
              <li><strong className="text-white">Right to Correction/Erasure:</strong> Update inaccurate data or request deletion (Note: Medical records cannot be deleted before the 3-year statutory period).</li>
              <li><strong className="text-white">Right to Withdraw Consent:</strong> You may revoke consent at any time, which may result in the termination of services.</li>
              <li><strong className="text-white">Right to Nominate:</strong> You may nominate an individual to exercise your rights in the event of death or incapacity.</li>
            </ol>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">8. Retention Policy</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-white">Clinical Records:</strong> Retained for a minimum of three (3) years from the date of the last entry as per NMC guidelines.</li>
              <li><strong className="text-white">Account Data:</strong> Retained for the duration of your active subscription.</li>
              <li><strong className="text-white">Audit Logs:</strong> Retained for one (1) year for cybersecurity compliance.</li>
            </ul>
          </section>

          <section className="bg-slate-900/50 rounded-xl p-5 border border-slate-800/50">
            <h2 className="text-lg font-semibold text-white mb-3">9. Grievance Redressal</h2>
            <p className="mb-2">In accordance with the IT Act, 2000 and DPDP Act, 2023, if you have any questions or complaints regarding this Privacy Policy, please contact our Grievance Officer:</p>
            <ul className="space-y-1">
              <li><strong className="text-slate-200">Name:</strong> Vikram Dagar</li>
              <li><strong className="text-slate-200">Email:</strong> onepricklabs@gmail.com</li>
              <li><strong className="text-slate-200">Address:</strong> Shop at Plot No. 14, KH 17/2 & 9, Sukhi Ram Park, Uttam Nagar, New Delhi - 110059.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">10. Changes to This Policy</h2>
            <p>We reserve the right to update this Privacy Policy. Any changes will be notified via the platform or email. Continued use of 1clin.com after updates constitutes acceptance of the revised policy.</p>
          </section>
        </div>

        <div className="mt-10 pt-6 border-t border-slate-800 text-center text-xs text-slate-600">
          &copy; {new Date().getFullYear()} Oneprick Labs Private Limited. All rights reserved.
        </div>
      </div>
    </div>
  );
}
